import { RSAA } from 'redux-api-middleware'
import { makeApiTypes } from 'sidecar-utility-library'

export const types = {
  ...makeApiTypes('FETCH_ADDRESS_SUGGESTIONS', 'address'),
  CLEAR_ADDRESS_SUGGESTIONS: 'address/CLEAR_ADDRESS_SUGGESTIONS',
  ...makeApiTypes('VERIFY_ADDRESS', 'address'),
  CLEAR_ADDRESS_VERIFICATION: 'address/CLEAR_ADDRESS_VERIFICATION',
  ...makeApiTypes('LOOKUP_ZIP', 'address'),
  ...makeApiTypes('LOOKUP_COUNTIES_BY_ZIP', 'address'),
}

export const fetchAddressSuggestions = (prefix, addr) => ({
  [RSAA]: {
    endpoint: 'https://api.lob.com/v1/us_autocompletions',
    method: 'POST',
    body: () => {
      const body = {
        address_prefix: prefix,
        geo_ip_sort: true,
      }
      if (addr && addr.state) {
        body.state = addr.state
      }
      return JSON.stringify(body)
    },
    headers: {
      Authorization: `Basic ${btoa(process.env.REACT_APP_LOB_API_KEY + ':')}`,
      token: 'IGNORE',
      tz: 'IGNORE',
    },
    types: [
      types.FETCH_ADDRESS_SUGGESTIONS_REQUEST,
      types.FETCH_ADDRESS_SUGGESTIONS_SUCCESS,
      types.FETCH_ADDRESS_SUGGESTIONS_ERROR,
    ],
    cache: true,
  },
})

export const clearAddressSuggestions = () => ({
  type: types.CLEAR_ADDRESS_SUGGESTIONS,
})

export const verifyAddress = address => ({
  [RSAA]: {
    endpoint: 'https://api.lob.com/v1/us_verifications',
    method: 'POST',
    body: JSON.stringify({
      primary_line: address.street,
      secondary_line: address.street2,
      city: address.city,
      state: address.state,
      // convert number to string because Lob requires a string
      zip_code: `${address.zip}`,
    }),
    headers: {
      Authorization: `Basic ${btoa(process.env.REACT_APP_LOB_API_KEY + ':')}`,
      token: 'IGNORE',
      tz: 'IGNORE',
    },
    types: [
      types.VERIFY_ADDRESS_REQUEST,
      types.VERIFY_ADDRESS_SUCCESS,
      types.VERIFY_ADDRESS_ERROR,
    ],
  },
})

export const clearAddressVerification = () => ({
  type: types.CLEAR_ADDRESS_VERIFICATION,
})

export const lookupZip = zip => ({
  [RSAA]: {
    endpoint: 'https://api.lob.com/v1/us_zip_lookups',
    method: 'POST',
    headers: {
      Authorization: `Basic ${btoa(process.env.REACT_APP_LOB_API_KEY + ':')}`,
      token: 'IGNORE',
      tz: 'IGNORE',
    },
    body: JSON.stringify({ zip_code: zip }),
    types: [
      types.LOOKUP_ZIP_REQUEST,
      types.LOOKUP_ZIP_SUCCESS,
      types.LOOKUP_ZIP_ERROR,
    ],
  },
})

export const listCountiesByZip = zip => ({
  [RSAA]: {
    endpoint: `https://marketplace.api.healthcare.gov/api/v1/counties/by/zip/${zip}?apikey=${process.env.REACT_APP_LIVE_MARKETPLACE_API_KEY}`,
    method: 'GET',
    headers: {
      token: 'IGNORE',
      tz: 'IGNORE',
    },
    types: [
      types.LOOKUP_COUNTIES_BY_ZIP_REQUEST,
      types.LOOKUP_COUNTIES_BY_ZIP_SUCCESS,
      types.LOOKUP_COUNTIES_BY_ZIP_ERROR,
    ],
  },
})

const initialState = {
  lookup: {
    busy: false,
    error: '',
    counties: [],
  },
  suggestions: {
    busy: false,
    error: '',
    entities: [],
  },
  verification: {
    busy: false,
    error: '',
    info: '',
  },
}

export default (state = initialState, { type, payload = {} } = {}) => {
  switch (type) {
    case types.FETCH_ADDRESS_SUGGESTIONS_REQUEST: {
      return {
        ...state,
        suggestions: {
          ...state.suggestions,
          busy: true,
        },
      }
    }
    case types.FETCH_ADDRESS_SUGGESTIONS_SUCCESS: {
      return {
        ...state,
        suggestions: {
          busy: false,
          error: null,
          entities: payload.suggestions,
        },
      }
    }
    case types.FETCH_ADDRESS_SUGGESTIONS_ERROR: {
      return {
        ...state,
        suggestions: {
          ...state.suggestions,
          busy: false,
          error:
            payload?.response?.message || 'Could not get address suggestions',
        },
      }
    }

    // ----------

    case types.VERIFY_ADDRESS_REQUEST: {
      return {
        ...state,
        verification: {
          ...state.verification,
          busy: true,
        },
      }
    }
    case types.VERIFY_ADDRESS_SUCCESS: {
      return {
        ...state,
        verification: {
          busy: false,
          error: null,
          info: payload,
        },
      }
    }
    case types.VERIFY_ADDRESS_ERROR: {
      return {
        ...state,
        verification: {
          busy: false,
          error: payload?.response?.message || 'Could not verify address',
        },
      }
    }

    // ----------

    case types.LOOKUP_ZIP_REQUEST:
    case types.LOOKUP_COUNTIES_BY_ZIP_REQUEST: {
      return {
        ...state,
        lookup: {
          ...state.lookup,
          busy: true,
          error: '',
        },
      }
    }
    case types.LOOKUP_ZIP_SUCCESS: {
      return {
        ...state,
        lookup: {
          ...state.lookup,
          error: null,
          busy: false,
        },
      }
    }
    case types.LOOKUP_ZIP_ERROR: {
      return {
        ...state,
        lookup: {
          ...state.lookup,
          busy: false,
          error: payload?.reponse?.message || 'Could not look up zip code',
        },
      }
    }

    // ----------

    case types.CLEAR_ADDRESS_SUGGESTIONS: {
      return {
        ...state,
        suggestions: {
          ...state.suggestions,
          entities: [],
        },
      }
    }

    case types.CLEAR_ADDRESS_VERIFICATION: {
      return {
        ...state,
        verification: {
          ...state.verification,
          info: '',
        },
      }
    }

    // ----------

    case types.LOOKUP_COUNTIES_BY_ZIP_SUCCESS: {
      return {
        ...state,
        lookup: {
          ...state.lookup,
          busy: false,
          counties: payload.counties,
        },
      }
    }
    case types.LOOKUP_COUNTIES_BY_ZIP_ERROR: {
      return {
        ...state,
        lookup: {
          ...state.lookup,
          busy: false,
          error: payload?.response?.message || 'Could not look up counties',
        },
      }
    }

    default:
      return state
  }
}
